import { useEffect, useMemo, useState } from 'react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  LoadingState,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleTable,
} from '@/components/ui'
import type { BwaPosition } from './types.ts'

interface ArticleGroup {
  id: string
  sapGroupCode: number | null
  name: string
}

interface RevenueGroupMapping {
  positionCode: string
  sapGroupCode: number
}

const NONE = '__none__'

/**
 * Maps each SAP item group to exactly one revenue position
 * (e.g. item groups X,Y,Z -> Contracts, A,B,C -> expected services).
 * Bulk-replace via PUT /liquidity-planning/revenue-group-mapping.
 */
export function RevenueGroupMappingTab({
  companyKey, positions, onSaved,
}: {
  companyKey: string
  positions: BwaPosition[]
  onSaved?: () => void
}) {
  const [groups, setGroups] = useState<ArticleGroup[]>([])
  const [assign, setAssign] = useState<Map<number, string>>(new Map())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Zielpositionen: alle Umsatz-Unterpositionen (Kinder von umsatzerloese).
  const revenuePositions = useMemo(
    () => positions.filter((p) => p.parentCode === 'umsatzerloese' && !p.isAggregate),
    [positions],
  )

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const [grpRes, mapRes] = await Promise.all([
        apiClient.get<ArticleGroup[]>('/articles/groups'),
        apiClient.get<RevenueGroupMapping[]>(
          `/liquidity-planning/revenue-group-mapping?company=${encodeURIComponent(companyKey)}`,
        ),
      ])
      const grps = (Array.isArray(grpRes.data) ? grpRes.data : []).filter((g) => g.sapGroupCode != null)
      setGroups(grps)
      const m = new Map<number, string>()
      for (const row of Array.isArray(mapRes.data) ? mapRes.data : []) {
        m.set(row.sapGroupCode, row.positionCode)
      }
      setAssign(m)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyKey])

  function setGroupPosition(sapGroupCode: number, positionCode: string) {
    setAssign((prev) => {
      const next = new Map(prev)
      if (positionCode === NONE) next.delete(sapGroupCode)
      else next.set(sapGroupCode, positionCode)
      return next
    })
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const entries = Array.from(assign.entries()).map(([sapGroupCode, positionCode]) => ({
        sapGroupCode,
        positionCode,
      }))
      await apiClient.put(
        `/liquidity-planning/revenue-group-mapping?company=${encodeURIComponent(companyKey)}`,
        { entries },
      )
      onSaved?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState label="Warengruppen werden geladen …" />

  return (
    <div className="space-y-3">
      <p className="text-small text-gray-500">
        Ordne jede Warengruppe einer Umsatz-Position zu. So fließen z.B. Warengruppen
        für wiederkehrende Leistungen in „Verträge", projektbezogene in
        „voraussichtliche Dienstleistungen". Nicht zugeordnete Gruppen bleiben unberücksichtigt.
      </p>
      {error && <p className="text-small text-red-600">{error}</p>}
      <SimpleTable>
        <SimpleTable.Head>
          <SimpleTable.Tr>
            <SimpleTable.Th>Warengruppe</SimpleTable.Th>
            <SimpleTable.Th>SAP-Code</SimpleTable.Th>
            <SimpleTable.Th>Umsatz-Position</SimpleTable.Th>
          </SimpleTable.Tr>
        </SimpleTable.Head>
        <SimpleTable.Body>
          {groups.length === 0 && (
            <SimpleTable.Tr>
              <SimpleTable.Td colSpan={3} className="text-center text-small text-gray-500 py-6">
                Keine Warengruppen gefunden. Bitte unter Artikel → Warengruppen pflegen / aus SAP laden.
              </SimpleTable.Td>
            </SimpleTable.Tr>
          )}
          {groups.map((g) => (
            <SimpleTable.Tr key={g.id}>
              <SimpleTable.Td>{g.name}</SimpleTable.Td>
              <SimpleTable.Td className="text-small text-gray-600">{g.sapGroupCode}</SimpleTable.Td>
              <SimpleTable.Td>
                <SelectRoot
                  value={assign.get(g.sapGroupCode!) ?? NONE}
                  onValueChange={(v) => setGroupPosition(g.sapGroupCode!, v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— nicht zugeordnet —</SelectItem>
                    {revenuePositions.map((p) => (
                      <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              </SimpleTable.Td>
            </SimpleTable.Tr>
          ))}
        </SimpleTable.Body>
      </SimpleTable>
      <div className="flex justify-end">
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Speichere…' : 'Zuordnung speichern'}
        </Button>
      </div>
    </div>
  )
}
